import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { InnogyComponentRendering } from '@core/jss-models';
import { FormGroupState } from 'ngrx-forms';
import type { TextField } from '@sitecore-jss/sitecore-jss-angular';
import { TrackToolStep } from '@analytics/models';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { CommonUiUtilityComponentsModule } from '@legacy/common-ui-utility-components';

import type { RadioItem } from '../radio/radio.model';
import type {
  FormAddress,
  AddressValidationResult,
} from './state/address-form.types';
import { AddressTypes } from './state/address-form.types';
import { AddressFormContent } from './model/address-form.model';
import { RadioComponent } from '../radio/radio.component';
import { PostalAddressFormComponent } from '../postal-address-form/postal-address-form.component';
import { TextComponent } from '../text/text.component';
import { InputErrorComponent } from '../input-error/input-error.component';

@Component({
  selector: 'wl-address-form[rendering][formState][content]',
  templateUrl: './address-form.component.html',
  styleUrls: [
    './address-form.component.ed.scss',
    './address-form.component.essent.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RadioComponent,
    PostalAddressFormComponent,
    CommonUiUtilityComponentsModule,
    FaIconComponent,
    TextComponent,
    InputErrorComponent,
  ],
})
export class AddressFormComponent implements OnInit {
  AddressType = AddressTypes;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() trackStep!: TrackToolStep;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() formState!: FormGroupState<FormAddress>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering?: InnogyComponentRendering;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() content!: AddressFormContent;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showAddressTypeSelector = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() sendValues = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() hashValues = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() floatingLabels = false;

  radioItems: RadioItem[] = [];
  automationId = '';

  get addressValidationResult() {
    return this.formState.userDefinedProperties[
      'ADDRESS_VALIDATION_RESULT'
    ] as AddressValidationResult;
  }

  ngOnInit(): void {
    this.automationId = this.formState.id;
    this.radioItems = [
      {
        id: 'addresstype-address',
        label: this.content?.AddressOptionLabel ?? '',
        value: AddressTypes.ADDRESS,
        trackingLabel: 'address',
      },
      {
        id: 'addresstype-pobox',
        label: this.content?.PoboxOptionLabel ?? '',
        value: AddressTypes.POBOX,
        trackingLabel: 'pobox',
      },
    ];
  }

  streetAndCityInputsVisible() {
    return this.hasStreetOrCityValue() || this.addressIsValidated();
  }

  private addressIsValidated() {
    return (
      this.formState.isValidationPending === false &&
      this.addressValidationResult !== undefined
    );
  }

  getCommunicationNumberLabel() {
    return this.formState.value.addressType === AddressTypes.ADDRESS
      ? this.content?.HouseNumberLabel
      : this.content?.PoboxLabel;
  }

  getCommunicationNumberRequiredError() {
    return this.formState.value.addressType === AddressTypes.ADDRESS
      ? this.content?.HouseNumberRequiredError
      : this.content?.PoboxRequiredError;
  }

  private hasStreetOrCityValue() {
    return this.formState.value.street || this.formState.value.city;
  }

  getAddressNotFoundErrorTitle(): TextField {
    return { value: this.content?.AddressNotFoundErrorTitle };
  }

  getAddressNotFoundErrorMessage(): TextField {
    return { value: this.content?.AddressNotFoundErrorMessage };
  }

  showAddressNotFoundError() {
    return (
      !this.formState.isValidationPending &&
      this.addressValidationResult === 'NOT_FOUND'
    );
  }
}
